import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import "../styles/MissingCoordsView.css";
import apiService from "../services/apiService";
import EditCoordinatesModal from "./EditCoordinatesModal";

const MissingCoordsView = () => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [validatingIds, setValidatingIds] = useState(new Set());

  useEffect(() => {
    loadAddresses();
  }, []);

  const loadAddresses = async () => {
    setLoading(true);
    try {
      console.log(
        "🔍 Cargando direcciones sin coordenadas de los últimos 180 días...",
      );
      const addressesData = await apiService.getMissingCoordinates();

      setAddresses(addressesData);

      console.log(
        `📊 Total: ${addressesData.length} direcciones sin coordenadas`,
      );

      if (addressesData.length === 0) {
        toast.success(
          "¡Excelente! Todas las direcciones tienen coordenadas GPS",
        );
      } else {
        toast.info(`${addressesData.length} direcciones sin coordenadas GPS`);
        triggerAutoValidation();
      }
    } catch (error) {
      console.error("Error cargando direcciones:", error);
      toast.error("Error al cargar direcciones sin coordenadas");
      setAddresses([]);
    } finally {
      setLoading(false);
    }
  };

  const triggerAutoValidation = async () => {
    setValidating(true);
    try {
      const result = await apiService.validateMissingCoordinates();
      if (result && result.results && result.results.updated > 0) {
        toast.info(
          `📍 ${result.results.updated} dirección(es) corregida(s) automáticamente con Google Places`,
          { autoClose: 5000 },
        );
        const updated = await apiService.getMissingCoordinates();
        setAddresses(updated);
      }
    } catch (err) {
      console.warn("Validación Places:", err.message);
    } finally {
      setValidating(false);
    }
  };

  const handleEditCoordinates = (address) => {
    setEditingAddress(address);
  };

  const handleCloseModal = () => {
    setEditingAddress(null);
  };

  const handleCoordinatesUpdated = () => {
    loadAddresses();
  };

  const handleValidate = async (addressId) => {
    setValidatingIds((prev) => new Set(prev).add(addressId));
    try {
      await apiService.markAddressValidated(addressId, "VALIDATED");
      toast.success("Dirección marcada como VALIDADA");
      setAddresses((prev) => prev.filter((a) => a.id_address !== addressId));
    } catch (error) {
      toast.error("Error al actualizar el estado de validación");
    } finally {
      setValidatingIds((prev) => {
        const next = new Set(prev);
        next.delete(addressId);
        return next;
      });
    }
  };

  return (
    <div className='missing-coords-view'>
      <div className='view-header'>
        <h2>Direcciones sin Coordenadas GPS (Últimos 180 días)</h2>
        <p className='view-subtitle'>
          Direcciones de clientes que han comprado en los últimos 6 meses pero
          no tienen coordenadas GPS
        </p>
      </div>

      {validating && (
        <div className='validation-banner'>
          🔍 Validando coordenadas con Google Places...
        </div>
      )}

      {/* Estadísticas */}
      <div className='stats-section'>
        <div className='stats-cards'>
          <div className='stat-card warning'>
            <div className='stat-icon'>⚠️</div>
            <div className='stat-content'>
              <div className='stat-label'>Direcciones Pendientes</div>
              <div className='stat-value'>{addresses.length}</div>
            </div>
          </div>

          <div className='stat-card info'>
            <div className='stat-icon'>🏠</div>
            <div className='stat-content'>
              <div className='stat-label'>Clientes Únicos</div>
              <div className='stat-value'>
                {new Set(addresses.map((a) => a.customer_email)).size}
              </div>
            </div>
          </div>

          <div className='stat-card success'>
            <div className='stat-icon'>🛒</div>
            <div className='stat-content'>
              <div className='stat-label'>Total Compras</div>
              <div className='stat-value'>
                {addresses.reduce(
                  (sum, a) => sum + (a.total_purchases || 0),
                  0,
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de direcciones */}
      {loading ? (
        <div className='loading-state'>
          <div className='loading-icon'>⏳</div>
          <p>Cargando direcciones...</p>
        </div>
      ) : addresses.length === 0 ? (
        <div className='empty-state'>
          <div className='empty-icon'>🎉</div>
          <h3>¡Excelente trabajo!</h3>
          <p>Todas las direcciones activas tienen coordenadas GPS asignadas.</p>
        </div>
      ) : (
        <div className='orders-section'>
          <div className='section-header'>
            <h3>Direcciones que requieren coordenadas ({addresses.length})</h3>
            <button
              className='btn-refresh'
              onClick={loadAddresses}
              disabled={loading}
            >
              🔄 Actualizar
            </button>
          </div>

          <div className='orders-table'>
            <table>
              <thead>
                <tr>
                  <th className='col-customer'>Cliente</th>
                  <th className='col-address'>Dirección</th>
                  <th className='col-district'>Distrito</th>
                  <th className='col-stats'>Compras</th>
                  <th className='col-date'>Última Compra</th>
                  <th className='col-actions'>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {addresses.map((address) => (
                  <tr key={address.id_address} className='order-row'>
                    <td className='col-customer'>
                      <strong>{address.customer_name || "Cliente"}</strong>

                      {address.leave_your_order && (
                        <div className='delivery-note'>
                          💬 {address.leave_your_order}
                        </div>
                      )}
                    </td>
                    <td className='col-address'>
                      <div className='address-text'>
                        {address.full_address || "Sin dirección"}
                      </div>

                      {(!address.latitude || !address.longitude) && (
                        <div className='no-coords-warning'>
                          ⚠️ No tiene coordenadas
                        </div>
                      )}
                    </td>
                    <td className='col-district'>
                      <span className='district-badge'>
                        📍 {address.district || "N/A"}
                      </span>
                    </td>
                    <td className='col-stats'>
                      <span className='purchase-count'>
                        {address.total_purchases}{" "}
                        {address.total_purchases === 1 ? "compra" : "compras"}
                      </span>
                    </td>
                    <td className='col-date'>
                      <div className='last-purchase'>
                        {moment(address.last_purchase_date).format(
                          "DD/MM/YYYY",
                        )}
                      </div>
                      {address.hour_programmed_start && (
                        <div className='typical-time'>
                          ⏰ {address.hour_programmed_start} -{" "}
                          {address.hour_programmed_end}
                        </div>
                      )}
                    </td>
                    <td className='col-actions'>
                      <button
                        className='btn-edit'
                        onClick={() => handleEditCoordinates(address)}
                        title='Agregar coordenadas GPS'
                      >
                        📍 Agregar GPS
                      </button>
                      <button
                        className='btn-validate'
                        onClick={() => handleValidate(address.id_address)}
                        disabled={validatingIds.has(address.id_address)}
                        title='Marcar como validada visualmente'
                      >
                        {validatingIds.has(address.id_address)
                          ? "..."
                          : "✓ Validado"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de edición de coordenadas */}
      {editingAddress && (
        <EditCoordinatesModal
          order={editingAddress}
          onClose={handleCloseModal}
          onSuccess={handleCoordinatesUpdated}
          allowManual
        />
      )}
    </div>
  );
};

export default MissingCoordsView;
